// ─────────────────────────────────────────────────────────────────────────────
// LabThree — Guida "Gestire una non conformità"
// Centro assistenza → guida per operatori e amministratori.
//
// Le schermate sono RICOSTRUITE (vedi la nota in atomi.js). Le etichette fra
// virgolette sono copiate dal codice: se una non corrisponde più a quello che
// si vede a schermo, va corretta qui.
//
// Riferimenti nel codice, per chi dovrà aggiornarla:
//   Registro e dettaglio ..... App.js, componente RegistroNC (e CardNC)
//   Nuova NC manuale ......... App.js, dentro RegistroNC
//   NC da valore fuori soglia  App.js, componente FormNC
//   NC da scarto produzione .. backend/routes/tracciabilita.js, /scarta
//   NC da parametro di fase .. backend/routes/tracciabilita.js, /chiudi-con-nc
//   Statistiche .............. App.js, componente StatisticheNc
//   Categorie e origini ...... backend/migrations/07_categorie_nc.sql,
//                              13_origine_nc.sql (tabelle GLOBALI)
//   Permessi ................. backend/routes/nc.js (blindato il 17/08/2026)
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import LT from '../../ui/tokens';
import I, { Ico } from '../../ui/icons';
import {
  GuidaLayout, H, P, Strong, Code, Tasto, Callout, Passo,
  MockFrame, Schermo, SchermoTitolo, FAQ,
} from './atomi';

const SEZIONI = [
  { id: 'n0', label: 'Prima di iniziare' },
  { id: 'n1', label: "1. Che cos'è, e le due etichette" },
  { id: 'n2', label: '2. Da dove nasce' },
  { id: 'n3', label: '3. Aprirne una a mano' },
  { id: 'n4', label: '4. Compilarla bene' },
  { id: 'n5', label: '5. Chiuderla' },
  { id: 'n6', label: '6. Gli allegati' },
  { id: 'n7', label: '7. Stampare il registro' },
  { id: 'n8', label: '8. Le statistiche' },
  { id: 'n9', label: '9. Chi vede cosa' },
  { id: 'n10', label: 'Le trappole' },
  { id: 'n11', label: 'Domande frequenti' },
];

const CATEGORIE = [
  'Corpi estranei', 'Igiene superfici', 'Igiene del personale',
  'Attrezzatura', 'Prodotto finito', 'Materia prima', 'Altro',
];

const ORIGINI = [
  'Autocontrollo / monitoraggio interno',
  'Visita Ispettiva Interna (VII)',
  'Audit esterno / ente di certificazione',
  'Reclamo cliente',
  'Analisi di laboratorio',
  'Ispezione organi di controllo (ASL/NAS)',
  'Altro',
];

const Elenco = ({ voci, colore }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, margin: '10px 0 4px' }}>
    {voci.map(v => (
      <span key={v} style={{
        padding: '4px 10px', borderRadius: 999, fontSize: 12.5, fontWeight: 600,
        background: colore.bg, color: colore.fg, border: `1px solid ${colore.bd}`,
      }}>{v}</span>
    ))}
  </div>
);

const CampoFinto = ({ etichetta, valore, placeholder, alto }) => (
  <div style={{ marginBottom: 9 }}>
    <div style={{
      fontSize: 10.5, fontWeight: 700, color: LT.n600,
      textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 3,
    }}>{etichetta}</div>
    <div style={{
      border: `1px solid ${LT.n300}`, borderRadius: LT.r.md, padding: '7px 9px',
      background: LT.n0, fontSize: 13, minHeight: alto ? 44 : 'auto',
      color: valore ? LT.n900 : LT.n400, lineHeight: 1.45,
    }}>{valore || placeholder}</div>
  </div>
);

export default function NonConformita() {
  return (
    <GuidaLayout
      titolo="Gestire una non conformità"
      sottotitolo="Aprirla, descriverla in modo utile, registrare l'azione correttiva e chiuderla. È la parte del sistema che un ispettore guarda per prima."
      durata="~ 15 minuti"
      destinatario="Per operatori e amministratori"
      sezioni={SEZIONI}
    >

      {/* n0 */}
      <H id="n0">Prima di iniziare</H>

      <P>
        Una non conformità è un problema che si è verificato: una temperatura fuori norma, un
        corpo estraneo, un reclamo di un cliente, un esito di laboratorio negativo. Nel gergo
        di tutti i giorni "una NC".
      </P>
      <P>
        Registrarla serve a due cose, e vale la pena essere chiari su quale conta di più. La
        prima è pratica: tenere traccia di cosa è andato storto e di cosa avete fatto per
        rimediare. La seconda è formale: quando arriva un controllo, il registro delle non
        conformità è il documento che dimostra che il vostro autocontrollo funziona per davvero.
      </P>

      <Callout kind="info" titolo="Non serve preparare niente">
        A differenza di altre parti di LabThree, qui non c'è nulla da configurare prima.
        Categorie e origini sono già pronte all'uso, e una NC si può aprire dal primo giorno.
      </Callout>

      {/* n1 */}
      <H id="n1" cat="pulizie">1. Che cos'è, e le due etichette</H>

      <P>
        Ogni non conformità si può etichettare in due modi diversi, e la differenza è la cosa
        più importante di questa guida perché è quella che si confonde sempre.
      </P>

      <P style={{ marginTop: 20 }}>
        La <Strong>categoria</Strong> dice <Strong>che cosa</Strong> è successo.
      </P>
      <Elenco voci={CATEGORIE} colore={{ bg: LT.violaSoft, fg: LT.viola, bd: '#DDD3FA' }} />

      <P style={{ marginTop: 20 }}>
        L'<Strong>origine</Strong> dice <Strong>come l'avete scoperto</Strong>.
      </P>
      <Elenco voci={ORIGINI} colore={{ bg: LT.cianoSoft, fg: '#155E75', bd: '#B6E9F2' }} />

      <P>
        Un corpo estraneo trovato dal vostro operatore durante un controllo e un corpo estraneo
        segnalato da un cliente al telefono hanno la <em>stessa categoria</em> e{' '}
        <em>origine diversa</em>. Ed è una distinzione che conta: dieci non conformità trovate
        col vostro autocontrollo raccontano un sistema che funziona, dieci arrivate come reclami
        dei clienti raccontano l'opposto. È esattamente la lettura che fa un auditor.
      </P>

      <Callout kind="warn" titolo="Le due liste sono uguali per tutti">
        Categorie e origini sono liste comuni a tutte le aziende che usano LabThree, non
        personalizzabili dal cliente. Se ti manca una voce, la aggiungiamo noi. Nel dubbio usa{' '}
        <Code>Altro</Code> e scrivi il resto nella descrizione: meglio una NC con la categoria
        approssimativa che una NC non registrata.
      </Callout>

      {/* n2 */}
      <H id="n2" cat="temperature">2. Da dove nasce</H>

      <P>
        Le non conformità arrivano nel registro per tre strade. Due sono automatiche, e conviene
        conoscerle perché ci si arriva senza averlo deciso.
      </P>

      <Passo n={1} titolo="Da un monitoraggio fuori soglia">
        <P style={{ margin: '6px 0' }}>
          Quando registri un valore fuori dai limiti e premi <Tasto>Non conforme</Tasto>, si
          apre la finestra <Strong>"Non conformità"</Strong> con la descrizione{' '}
          <Strong>già scritta</Strong>: tipo di controllo, oggetto, collocazione, valore
          misurato, di quanto ha sforato e quali erano i limiti. Il testo è modificabile: puoi
          aggiungere quello che il sistema non può sapere.
        </P>
      </Passo>

      <Passo n={2} titolo="Da una lavorazione (solo con la Tracciabilità)">
        <P style={{ margin: '6px 0' }}>
          Due casi. Se <Strong>scarti del prodotto</Strong> durante una fase, la finestra ti
          avverte che "verrà creata automaticamente una Non Conformità": la motivazione che
          scrivi lì diventa la descrizione, insieme alle quantità. Se{' '}
          <Strong>chiudi una fase con un parametro fuori limite</Strong>, succede lo stesso, e
          la descrizione contiene anche il lotto — che è quello che serve se un giorno bisogna
          risalire.
        </P>
      </Passo>

      <Passo n={3} titolo="A mano, quando il problema non nasce da un controllo">
        <P style={{ margin: '6px 0' }}>
          Il reclamo di un cliente, l'esito di un'analisi, un rilievo di un audit. È il caso del
          punto 3 qui sotto.
        </P>
      </Passo>

      <Callout kind="tip" titolo="L'origine la mette il sistema, la categoria no">
        Su una NC nata da un monitoraggio l'origine è già compilata con{' '}
        <Strong>"Autocontrollo / monitoraggio interno"</Strong>, perché è da lì che arriva: puoi
        cambiarla, ma nel caso normale è giusta così. La <Strong>categoria</Strong> invece resta
        su "Non categorizzata", e quella tocca a te — il programma non può sapere se il problema
        è una temperatura, un corpo estraneo o l'igiene di una superficie. È un clic, e dopo un
        anno è la differenza fra avere un grafico che ti dice dove nascono i problemi e non
        averlo.
      </Callout>

      {/* n3 */}
      <H id="n3">3. Aprirne una a mano</H>

      <P>
        Vai su <Code>Non conformità</Code> e premi <Tasto>Nuova NC manuale</Tasto>. Il titolo
        della pagina che si apre chiarisce di che si tratta: "Registrazione manuale (non
        collegata a un'attività)".
      </P>

      <MockFrame url="app.labthree.it/CASEIFICIO" markers={[
        { n: 1, top: '30%', left: '50%', titolo: 'Le due etichette',
          testo: 'Sono dichiarate "opzionali" e tecnicamente lo sono, ma compilale: è quello che rende leggibili le statistiche fra sei mesi.' },
        { n: 2, top: '54%', left: '50%', titolo: "L'unico campo obbligatorio",
          testo: 'Senza descrizione il salvataggio si ferma con "Inserisci almeno la descrizione dell\'evento".' },
      ]}>
        <Schermo>
          <SchermoTitolo
            titolo="Nuova non conformità"
            sottotitolo="Registrazione manuale (non collegata a un'attività)"
          />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <CampoFinto etichetta="Data NC" valore="17/08/2026" />
            <div />
            <CampoFinto etichetta="Categoria (opzionale)" placeholder="— Non categorizzata —" />
            <CampoFinto etichetta="Origine (opzionale)" placeholder="— Non specificata —" />
          </div>
          <CampoFinto
            etichetta="Descrizione Non Conformità / Reclamo *"
            placeholder="Descrivere la non conformità o il reclamo" alto />
          <CampoFinto etichetta="Causa" placeholder="Causa del problema" alto />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 7, marginTop: 4 }}>
            <span style={{
              padding: '7px 14px', borderRadius: LT.r.md, fontSize: 12.5, fontWeight: 700,
              background: LT.blu, color: '#fff',
            }}>Salva NC</span>
          </div>
        </Schermo>
      </MockFrame>

      <P>
        Se lasci la data vuota il sistema mette oggi. Il tuo nome viene registrato da solo come
        compilatore: non c'è un campo "rilevato da" da riempire.
      </P>

      {/* n4 */}
      <H id="n4">4. Compilarla bene</H>

      <P>
        Oltre alla descrizione ci sono tre campi che nessuno è obbligato a compilare e che sono
        esattamente quelli che un ispettore legge. Vale la pena capire cosa ci va, perché la
        differenza fra un registro utile e un registro inutile sta tutta qui.
      </P>

      <P>
        <Strong>Causa</Strong> — perché è successo. Non "temperatura alta", che è il fatto, ma
        "guarnizione della porta rotta", che è la causa. È il campo che si lascia in bianco più
        spesso ed è quello che evita di ritrovarsi lo stesso problema fra un mese.
      </P>
      <P>
        <Strong>Trattamento immediato</Strong> — cosa avete fatto <em>sul momento</em> col
        prodotto o con l'attrezzatura. Spostato la merce in un'altra cella, bloccato il lotto,
        buttato via. Riguarda il singolo episodio.
      </P>
      <P>
        <Strong>Azione correttiva</Strong> — cosa fate perché <em>non ricapiti</em>. Sostituita
        la guarnizione, aggiunto un controllo serale, formato il personale. Riguarda il futuro.
      </P>

      <Callout kind="tip" titolo="La prova del nove">
        Se trattamento immediato e azione correttiva dicono la stessa cosa, l'azione correttiva
        manca. "Ho buttato il prodotto" non impedisce che succeda di nuovo domani.
      </Callout>

      {/* n5 */}
      <H id="n5" cat="formazione">5. Chiuderla</H>

      <P>
        Qui c'è una cosa che sorprende tutti: <Strong>non esiste un pulsante "Chiudi NC"</Strong>.
        Una non conformità è aperta o chiusa in base a un solo dato, la{' '}
        <Strong>data di risoluzione</Strong>. Se è vuota è aperta, se c'è è chiusa. Non ci sono
        stati intermedi.
      </P>

      <MockFrame didascalia='Nel dettaglio della NC, sezione "Risoluzione". Finché la data è vuota, sopra c’è scritto "Compilare per chiudere la NC".'>
        <Schermo>
          <div style={{
            background: LT.n0, border: `1px solid ${LT.n150}`, borderRadius: LT.r.lg, padding: 15,
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 9, marginBottom: 12,
              paddingBottom: 10, borderBottom: `1px solid ${LT.n150}`,
            }}>
              <Ico d={I.checkCircle} size={17} stroke={LT.verde} />
              <div style={{ fontSize: 15, fontWeight: 700, color: LT.navy }}>Risoluzione</div>
              <div style={{ fontSize: 12, color: LT.n500 }}>· Compilare per chiudere la NC</div>
            </div>
            <CampoFinto etichetta="Data risoluzione" placeholder="gg/mm/aaaa" />
            <CampoFinto etichetta="Esito risoluzione"
              placeholder="Descrivere l'esito della risoluzione" alto />
            <CampoFinto etichetta="Note risoluzione e valutazione efficacia" placeholder="" alto />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 7 }}>
              <span style={{
                padding: '7px 13px', borderRadius: LT.r.md, fontSize: 12.5, fontWeight: 600,
                background: LT.n0, color: LT.n700, border: `1px solid ${LT.n300}`,
              }}>Annulla</span>
              <span style={{
                padding: '7px 14px', borderRadius: LT.r.md, fontSize: 12.5, fontWeight: 700,
                background: LT.blu, color: '#fff',
              }}>Salva</span>
            </div>
          </div>
        </Schermo>
      </MockFrame>

      <P>
        Quindi: compili <Strong>"Data risoluzione"</Strong>, scrivi l'esito, e premi{' '}
        <Tasto>Salva</Tasto>. La NC passa da "Aperta" a "Chiusa" e il chip rosso diventa verde.
      </P>
      <P>
        Il terzo campo, <Strong>"Note risoluzione e valutazione efficacia"</Strong>, è il posto
        dove si scrive se la contromisura ha funzionato. Non è un passaggio separato che il
        sistema ti chiede: è un campo di testo che nessuno controlla. Ma è quello che distingue
        "abbiamo fatto qualcosa" da "abbiamo verificato che quel qualcosa serviva", e all'occhio
        di chi valuta la differenza è enorme.
      </P>

      <Callout kind="info" titolo="Riaprire una NC chiusa">
        Si può, e non c'è un pulsante nemmeno per questo: si svuota la data di risoluzione e si
        salva. Nessuna conferma, nessun avviso. Utile se ti accorgi che il problema non era
        davvero risolto.
      </Callout>

      <Callout kind="warn" titolo="Nessuno ti fermerà">
        Puoi chiudere una NC lasciando causa, trattamento e azione correttiva completamente in
        bianco: basta la data. Il programma non fa storie e non c'è nessun messaggio di blocco.
        È voluto — non tutte le NC richiedono lo stesso rigore — ma vuol dire che la qualità del
        registro dipende da voi, non dal software.
      </Callout>

      {/* n6 */}
      <H id="n6">6. Gli allegati</H>

      <P>
        A ogni non conformità si possono attaccare file: la foto del corpo estraneo, la mail del
        reclamo, il referto del laboratorio, la fattura della riparazione. È la sezione{' '}
        <Strong>"Allegati"</Strong> nel dettaglio, e si usa trascinando il file sopra il riquadro
        o premendo <Tasto>sfoglia</Tasto>.
      </P>
      <P>
        Il limite dichiarato è di <Strong>10 MB per file</Strong>. Vanno bene PDF, immagini
        (jpg, png, gif), documenti Word ed Excel, e anche i file di posta <Code>.eml</Code> e{' '}
        <Code>.msg</Code> — comodissimi per allegare il reclamo di un cliente così com'è
        arrivato, senza doverlo ricopiare.
      </P>

      <Callout kind="tip" titolo="La foto vale più della descrizione">
        Se il problema si vede, fotografalo col telefono e allegalo. Fra un anno "corpo estraneo
        metallico di circa 2 mm" dirà molto meno di una foto, sia a voi sia a chi controlla.
      </Callout>

      {/* n7 */}
      <H id="n7">7. Stampare il registro</H>

      <P>
        Il pulsante <Tasto>Stampa</Tasto>, sopra la lista, apre l'anteprima del registro con{' '}
        <Tasto>⬇ Scarica PDF</Tasto> e <Tasto>🖨 Stampa</Tasto>. Il documento contiene i dati
        dell'azienda, il periodo, le NC divise fra aperte e chiuse con tutti i loro campi, i
        totali e un riepilogo per categoria.
      </P>

      <Callout kind="warn" titolo="Si stampa quello che hai filtrato">
        La stampa segue <Strong>i filtri attivi in quel momento</Strong>, non tutto il registro.
        È utilissimo — puoi stampare solo il 2026, solo le aperte, solo una categoria — ma se
        hai lasciato un filtro impostato dalla ricerca di prima, te lo porti nel PDF senza
        accorgertene. Prima di stampare per un'ispezione, controlla che lo stato sia su{' '}
        <Tasto>Tutte</Tasto> e che le date coprano il periodo che ti serve.
      </Callout>

      {/* n8 */}
      <H id="n8" cat="produzione">8. Le statistiche</H>

      <P>
        Nel registro, accanto a <Tasto>Lista</Tasto>, gli amministratori hanno la linguetta{' '}
        <Tasto>Statistiche</Tasto>. Non è un vezzo: è il posto dove il registro smette di essere
        un obbligo e comincia a dirvi qualcosa.
      </P>
      <P>
        In alto quattro numeri: <Strong>Totale NC</Strong>, <Strong>Aperte</Strong>,{' '}
        <Strong>Chiuse</Strong> e <Strong>Tempo medio chiusura</Strong> in giorni. Sotto, la
        distribuzione per categoria e quella per origine, il trend mensile, e l'elenco delle{' '}
        <Strong>"NC aperte da più tempo"</Strong>, che segnala quante superano i 60 giorni.
      </P>
      <P>
        Quest'ultimo è il riquadro da guardare per primo ogni mese. Una non conformità aperta da
        quattro mesi non è un problema di software: o è stata risolta e nessuno ha compilato la
        data, oppure è davvero ancora lì. Entrambe le cose vanno sistemate, e la seconda è
        peggio della prima.
      </P>

      {/* n9 */}
      <H id="n9">9. Chi vede cosa</H>

      <P>
        Le regole sono tre e valgono davvero, non solo nell'aspetto dei pulsanti.
      </P>
      <P>
        Un <Strong>operatore</Strong> vede e modifica soltanto le non conformità che ha aperto
        lui. Le altre non gli compaiono nemmeno in elenco.
      </P>
      <P>
        Un <Strong>amministratore</Strong> vede e modifica tutte quelle della propria azienda, e
        ha in più le statistiche e la rinumerazione del registro.
      </P>
      <P>
        Se apri il dettaglio di una NC che non puoi modificare, accanto alla data compare{' '}
        <Strong>"· Sola lettura"</Strong> e i pulsanti di salvataggio non ci sono.
      </P>

      {/* n10 */}
      <H id="n10">Le trappole</H>

      <div style={{ margin: '16px 0' }}>
        {[
          ['La categoria non la indovina nessuno',
            "L'origine viene precompilata, la categoria no: scegliela prima di confermare, altrimenti il grafico per categoria resta vuoto."],
          ['Non esiste il pulsante "Chiudi NC"',
            'Si chiude compilando "Data risoluzione" e premendo Salva. Si riapre svuotando la stessa data.'],
          ['La stampa segue i filtri attivi',
            'Se hai un filtro impostato, il PDF contiene solo quelle NC. Controlla i filtri prima di stampare per un\'ispezione.'],
          ['Un operatore non trova la NC di un collega',
            'È il comportamento previsto: ognuno vede le proprie. Serve un amministratore per vedere tutto il registro.'],
          ['Si può chiudere una NC senza scrivere niente',
            'Basta la data. Nessun blocco. La qualità del registro dipende da chi lo compila.'],
        ].map(([t, d]) => (
          <div key={t} style={{
            display: 'flex', gap: 11, alignItems: 'flex-start',
            padding: '11px 0', borderBottom: `1px solid ${LT.n150}`,
          }}>
            <Ico d={I.alert} size={16} stroke={LT.arancio} style={{ marginTop: 3, flexShrink: 0 }} />
            <div>
              <div style={{ fontSize: 14.5, fontWeight: 700, color: LT.n900 }}>{t}</div>
              <div style={{ fontSize: 13.5, color: LT.n600, lineHeight: 1.55, marginTop: 2 }}>{d}</div>
            </div>
          </div>
        ))}
      </div>

      {/* n11 */}
      <H id="n11">Domande frequenti</H>

      <FAQ q="Quante non conformità è normale avere?">
        Non c'è un numero giusto, ma zero è il numero sbagliato. Un registro senza nemmeno una NC
        in due anni dice a chi controlla che i problemi non vengono registrati, non che non
        esistono. Poche NC ben documentate, con causa e azione correttiva, valgono più di un
        registro immacolato.
      </FAQ>

      <FAQ q="Devo aprire una NC anche per un problema piccolo che ho risolto subito?">
        Se riguarda la sicurezza alimentare, sì, e ci metti due minuti: descrizione, trattamento
        immediato, data di risoluzione, chiusa. Il registro mostra un sistema che intercetta le
        cose e le sistema, che è il punto.
      </FAQ>

      <FAQ q="Posso cambiare le categorie o aggiungerne una mia?">
        No, sono liste comuni a tutte le aziende che usano LabThree e le gestiamo noi. Se ti
        serve una voce che non c'è, chiedila: se ha senso in generale la aggiungiamo per tutti.
        Nel frattempo usa "Altro" e dettaglia nella descrizione.
      </FAQ>

      <FAQ q="A cosa serve il pulsante Rinumera?">
        Rinumera i progressivi del registro in ordine di data, anno per anno. Serve se le NC sono
        state inserite in disordine e la numerazione non segue le date. È riservato agli
        amministratori e si usa raramente: normalmente il numero lo assegna il sistema da solo.
      </FAQ>

      <FAQ q="Il numero della NC posso deciderlo io?">
        No, il progressivo lo assegna il sistema per azienda e per anno, nel formato{' '}
        <Code>#numero/anno</Code>. È voluto: un registro in cui i numeri si scelgono a mano non
        dimostra niente.
      </FAQ>

      <FAQ q="Ho chiuso una NC per sbaglio, come rimedio?">
        Svuota il campo "Data risoluzione" e premi Salva: torna aperta. Se non riesci perché la
        NC è di un collega, serve un amministratore.
      </FAQ>

      <div style={{
        marginTop: 40, padding: 18, background: LT.bluSoft,
        borderRadius: LT.r.lg, border: `1px solid ${LT.n150}`,
      }}>
        <div style={{ fontSize: 15.5, fontWeight: 700, color: LT.navy, marginBottom: 4 }}>
          E adesso?
        </div>
        <div style={{ fontSize: 14, color: LT.n700, lineHeight: 1.65 }}>
          Se sei amministratore, la cosa più utile che puoi fare da qui è aprire la linguetta{' '}
          <Strong>Statistiche</Strong> una volta al mese e guardare il riquadro delle NC aperte
          da più tempo. È il controllo che costa trenta secondi ed evita le sorprese.
        </div>
      </div>

    </GuidaLayout>
  );
}
